/**
 * Enable Healthcare Design System — catalog & runtime support.
 *
 * Exposes:
 *   • SIZES        — width/height/label for each canvas size
 *   • CATALOG      — array of 21 template descriptors with .render()
 *   • TemplateEdit — local-state provider per-template (auto-saves to localStorage)
 *   • Stage        — auto-scaling wrapper that fits a native-size canvas into
 *                    any parent width while preserving aspect ratio
 */
import { useState, useRef, useLayoutEffect, useCallback, forwardRef } from "react";
import { EditCtx } from "./templates.jsx";
import {
  GradientAnnouncement, HeadlineOverPhoto, BrowserHero, ProductFeature,
  StatCard, Testimonial, CarouselSlide, EventPromo, DeviceHero,
} from "./templates.jsx";
import {
  FullBleedImage, SplitImageText, SuccessStory, ProductMockup,
  WebinarBig, MagazineCover, ImageQuoteOverlay, ImageStat, FeatureGrid, PortraitImageHero,
} from "./templates-image.jsx";

export const SIZES = {
  square:   { w: 1080, h: 1080, label: "Square",   note: "1080×1080 · IG, LinkedIn" },
  portrait: { w: 1080, h: 1350, label: "Portrait", note: "1080×1350 · IG feed (4:5)" },
  story:    { w: 1080, h: 1920, label: "Story",    note: "1080×1920 · IG/FB stories, TikTok" },
  wide:     { w: 1200, h:  628, label: "Landscape",note: "1200×628 · LinkedIn, X, FB link" },
  yt:       { w: 1920, h: 1080, label: "Banner",   note: "1920×1080 · YouTube, banners" },
};

const STORAGE_KEY = "eh_social_edits_v2";
const loadAllEdits = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } };
const saveAllEdits = (e) => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(e)); } catch { /* ignore quota errors */ } };

export function TemplateEdit({ id, children }) {
  const [values, setValues] = useState(() => loadAllEdits()[id] || {});
  const set = useCallback((key, v) => {
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      const all = loadAllEdits();
      all[id] = next;
      saveAllEdits(all);
      return next;
    });
  }, [id]);
  const reset = useCallback(() => {
    setValues({});
    const all = loadAllEdits();
    delete all[id];
    saveAllEdits(all);
  }, [id]);
  return <EditCtx.Provider value={{ values, set, reset }}>{children}</EditCtx.Provider>;
}

/** Auto-scaling stage: renders the canvas at its native pixel size, then scales
 *  it down to fit the parent's width via CSS transform. Aspect-ratio preserved. */
export const Stage = forwardRef(function Stage({ size, children }, fwdRef) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(0.4);
  const { w, h } = SIZES[size] || SIZES.square;

  const setInner = useCallback((node) => {
    innerRef.current = node;
    if (typeof fwdRef === "function") fwdRef(node);
    else if (fwdRef) fwdRef.current = node;
  }, [fwdRef]);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / w);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [w]);

  return (
    <div ref={wrapRef} style={{ width: "100%", aspectRatio: `${w} / ${h}`, position: "relative", overflow: "hidden", borderRadius: 14 }}>
      <div
        ref={setInner}
        style={{
          width: w, height: h,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          position: "absolute", left: 0, top: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
});

// 21-template catalog — same content/defaults as the source zip.
export const CATALOG = [
  // SQUARE
  { id: "sq-announce", size: "square", name: "Announcement", layout: "Gradient + headline",
    render: () => <GradientAnnouncement size="square" defaults={{ eyebrow: "NOW IN GENERAL AVAILABILITY", headline: "Charting, written for you.", support: "ARIA, our AI voice assistant, drafts encounter notes while you focus on the patient.", cta: "ehiehr.com/ehr", note: "Spring · 2026" }} /> },
  { id: "sq-stat", size: "square", name: "Stat / proof", layout: "Number-led",
    render: () => <StatCard size="square" defaults={{ stat: "98%", label: "of pre-authorizations are approved on first submission, guaranteed.", source: "Source · Enable Healthcare RCM, FY 2026" }} /> },
  { id: "sq-testimonial", size: "square", name: "Testimonial", layout: "Quote + headshot",
    render: () => <Testimonial size="square" defaults={{ quote: "Charting used to eat my evenings. Now I leave the clinic when my last patient does.", name: "Dr. Anjali Mehta", role: "Internal Medicine", practice: "Cedar Park Primary Care", image: null }} /> },
  { id: "sq-fullbleed-aria", size: "square", name: "Full-bleed · ARIA", layout: "Full-bleed image",
    render: () => <FullBleedImage size="square" defaults={{ eyebrow: "ARIA · AI VOICE ASSISTANT", headline: "Charting, written for you.", image: "/eh-ds-assets/ccm-ipad.jpg", cta: "ehiehr.com/aria" }} /> },

  // PORTRAIT
  { id: "po-ehr", size: "portrait", name: "EHR — feature", layout: "Headline + photo",
    render: () => <HeadlineOverPhoto size="portrait" defaults={{ eyebrow: "ELECTRONIC HEALTH RECORD", headline: "Less time charting. More time caring.", image: "/eh-ds-assets/ccm-ipad.jpg", cta: "See it in action" }} /> },
  { id: "po-rcm", size: "portrait", name: "RCM — browser hero", layout: "Browser mockup",
    render: () => <BrowserHero size="portrait" defaults={{ eyebrow: "REVENUE CYCLE MANAGEMENT", headline: "Consistent positive cash flow.", url: "ehiehr.com/rcm", productImage: "/eh-ds-assets/rcm-surface.jpg" }} /> },
  { id: "po-multidevice", size: "portrait", name: "Multi-device", layout: "Hero with devices",
    render: () => <DeviceHero size="portrait" defaults={{ eyebrow: "ONE PLATFORM, EVERY DEVICE", headline: "Built for the way your practice actually works.", support: "EHR, billing, patient engagement and care coordination — built to work together.", deviceImage: "/eh-ds-assets/multi-device.png" }} /> },
  { id: "po-success", size: "portrait", name: "Success story · portrait", layout: "Image quote",
    render: () => <SuccessStory size="portrait" defaults={{ image: "/eh-ds-assets/ccm-ipad.jpg", quote: "Our CCM enrollment doubled in 90 days.", name: "Dr. Anjali Mehta", role: "Internal Medicine", practice: "Cedar Park Primary Care" }} /> },

  // STORY
  { id: "st-headline", size: "story", name: "Story · headline", layout: "Gradient + headline",
    render: () => <GradientAnnouncement size="story" defaults={{ eyebrow: "THE ONLY EHR THAT WORKS WITH YOU", headline: "The dream EHR is finally here.", support: "AI-powered charting, intelligent coding, and a branded mobile app for your practice.", cta: "ehiehr.com/ehr", note: "" }} /> },
  { id: "st-stat", size: "story", name: "Story · stat", layout: "Big number",
    render: () => <StatCard size="story" defaults={{ stat: "−38%", label: "reduction in average charting time, across early-adopter practices.", source: "Internal benchmarks · 2026" }} /> },
  { id: "st-ccm", size: "story", name: "Story · CCM photo", layout: "Headline + photo",
    render: () => <HeadlineOverPhoto size="story" defaults={{ eyebrow: "CHRONIC CARE MANAGEMENT", headline: "Care that reaches beyond your office.", image: "/eh-ds-assets/ccm-ipad.jpg", cta: "Try CCM risk-free" }} /> },
  { id: "st-webinar", size: "story", name: "Story · webinar", layout: "Event promo",
    render: () => <EventPromo size="story" defaults={{ eventType: "WEBINAR", title: "Cutting charting time in half: an AI-EHR demo.", date: "Thu, May 14", time: "2:00 PM ET", presenter: "Dr. Marcus Chen", role: "Chief Medical Officer, Enable Healthcare", cta: "Reserve your seat", eventUrl: "ehiehr.com/events" }} /> },

  // WIDE
  { id: "wd-ehr", size: "wide", name: "Link card · EHR", layout: "Browser hero",
    render: () => <BrowserHero size="wide" defaults={{ eyebrow: "MEET YOUR EHR", headline: "Lower your charting time. Keep your practice.", url: "ehiehr.com/ehr" }} /> },
  { id: "wd-rpm", size: "wide", name: "Link card · RPM", layout: "Product feature",
    render: () => <ProductFeature size="wide" defaults={{ eyebrow: "REMOTE PATIENT MONITORING", headline: "Real-time monitoring. Real reimbursement.", description: "Cellular-enabled devices and an AI-driven RPM app — patient enrollment and billing handled end-to-end.", image: "/eh-ds-assets/rcm-iso.png" }} /> },
  { id: "wd-stat", size: "wide", name: "Link card · Stat", layout: "Number-led",
    render: () => <StatCard size="wide" defaults={{ stat: "50%", label: "increase in CMS reimbursements with our Chronic Care Management program.", source: "CMS · FY 2022 reimbursement adjustment" }} /> },

  // YT
  { id: "yt-banner", size: "yt", name: "YouTube banner", layout: "Multi-device hero",
    render: () => <DeviceHero size="yt" defaults={{ eyebrow: "HEALTHCARE TECH SOLUTIONS", headline: "The only EHR that works with you.", support: "EHR · RCM · CCM · RPM · Patient Engagement", deviceImage: "/eh-ds-assets/multi-device.png" }} /> },
  { id: "yt-stat", size: "yt", name: "Web banner · stat", layout: "Number-led",
    render: () => <StatCard size="yt" defaults={{ stat: "50%", label: "increase in CMS reimbursements with our Chronic Care Management program.", source: "CMS · FY 2022 reimbursement adjustment" }} /> },

  // Carousel
  { id: "sq-carousel-1", size: "square", name: "Carousel · slide 1", layout: "Carousel intro",
    render: () => <CarouselSlide size="square" slideNumber={1} totalSlides={5} defaults={{ eyebrow: "BUILT FOR YOU", title: "5 ways AI is changing how you chart.", subtitle: "Swipe to learn more →" }} /> },

  // More image-forward
  { id: "sq-split", size: "square", name: "Split · image + text", layout: "50/50",
    render: () => <SplitImageText size="square" defaults={{ image: "/eh-ds-assets/rcm-surface.jpg", eyebrow: "RCM", headline: "Get paid faster.", support: "AI-driven claims with first-pass approval guaranteed.", cta: "Learn more" }} /> },
  { id: "po-mockup", size: "portrait", name: "Product mockup", layout: "Device showcase",
    render: () => <ProductMockup size="portrait" defaults={{ eyebrow: "EHR · MOBILE", headline: "Your practice, in your pocket.", image: "/eh-ds-assets/multi-device.png" }} /> },
  { id: "st-magazine", size: "story", name: "Magazine cover", layout: "Editorial",
    render: () => <MagazineCover size="story" defaults={{ issue: "ISSUE 12 · SPRING 2026", title: "The AI-first practice.", byline: "By Dr. Marcus Chen", image: "/eh-ds-assets/ccm-ipad.jpg" }} /> },
  { id: "sq-imagestat", size: "square", name: "Image · stat overlay", layout: "Stat on photo",
    render: () => <ImageStat size="square" defaults={{ image: "/eh-ds-assets/rcm-surface.jpg", stat: "98%", label: "First-pass approval rate.", source: "RCM FY 2026" }} /> },
];

/** Find a template by id. */
export const getTemplate = (id) => CATALOG.find((t) => t.id === id);

/** Group catalog by size — used by the size-picker in Content Studio. */
export const CATALOG_BY_SIZE = Object.keys(SIZES).reduce((acc, sz) => {
  acc[sz] = CATALOG.filter((t) => t.size === sz);
  return acc;
}, {});
